import { reportError } from "@/utils/reportError";
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useState } from 'react';

interface MarkResult {
  success: boolean;
  alreadyTested?: boolean;
}

/**
 * Tracks which TEST_APPS the current user has already marked as tested.
 * One mark per (user_id, app_id) — enforced by UNIQUE constraint server-side.
 */
export function useAppTests() {
  const [testedAppIds, setTestedAppIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const fetchTested = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setTestedAppIds(new Set());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('app_tests')
        .select('app_id')
        .eq('user_id', user.id);

      if (error) {
        reportError(error);
        return;
      }

      setTestedAppIds(new Set((data ?? []).map((row) => row.app_id as string)));
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTested();
  }, [fetchTested]);

  const markAsTested = useCallback(async (appId: string): Promise<MarkResult> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return { success: false };

    // Optimistic update
    setTestedAppIds((prev) => {
      const next = new Set(prev);
      next.add(appId);
      return next;
    });

    const { error } = await supabase
      .from('app_tests')
      .insert({ user_id: user.id, app_id: appId });

    if (error) {
      // 23505 = unique_violation → déjà marqué, on garde l'état optimistic
      if (error.code === '23505') {
        return { success: true, alreadyTested: true };
      }
      reportError(error);
      // rollback
      setTestedAppIds((prev) => {
        const next = new Set(prev);
        next.delete(appId);
        return next;
      });
      return { success: false };
    }

    return { success: true };
  }, []);

  return {
    testedAppIds,
    loading,
    markAsTested,
    refetch: fetchTested,
  };
}
